import React, { createContext, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import i18n, { RTL_LANGS } from "@/i18n";

const LanguageContext = createContext();

// 10 supported languages (navigation.md). AR + UR are RTL. Labels are native names.
export const LANGUAGES = [
  { code: "en", label: "English", dir: "ltr" },
  { code: "zh", label: "中文", dir: "ltr" },
  { code: "hi", label: "हिन्दी", dir: "ltr" },
  { code: "es", label: "Español", dir: "ltr" },
  { code: "fr", label: "Français", dir: "ltr" },
  { code: "ar", label: "العربية", dir: "rtl" },
  { code: "bn", label: "বাংলা", dir: "ltr" },
  { code: "ru", label: "Русский", dir: "ltr" },
  { code: "pt", label: "Português", dir: "ltr" },
  { code: "ur", label: "اردو", dir: "rtl" },
];

const BACKEND = process.env.REACT_APP_BACKEND_URL;

async function syncProfileLanguage(code) {
  const token = localStorage.getItem("samtwo_token");
  if (!token) return;
  try {
    await fetch(`${BACKEND}/api/auth/subscriber/language`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ language: code }),
    });
  } catch { /* non-blocking */ }
}

function applyDir(code) {
  // Owner decision: text reads RTL (intrinsic Unicode bidi handles Arabic/Urdu
  // word order on its own), but layout must NOT mirror. Setting dir="rtl" would
  // flip flex/grid order, alignment and logical box properties, moving the menu
  // and repositioning titles/descriptions. So we keep the document LTR and never
  // mirror; only `lang` is set (for fonts/hyphenation/a11y).
  document.documentElement.dir = "ltr";
  document.documentElement.lang = code;
}

export function LanguageProvider({ children }) {
  const { t, i18n: inst } = useTranslation();
  const lang = inst.language ? inst.language.split("-")[0] : "en";

  useEffect(() => {
    applyDir(lang);
  }, [lang]);

  // Logged-in users: apply their saved preference on load (cross-device).
  useEffect(() => {
    const token = localStorage.getItem("samtwo_token");
    if (!token) return;
    fetch(`${BACKEND}/api/auth/subscriber/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((u) => {
        const pref = u && u.language_preference;
        if (pref && pref !== i18n.language) i18n.changeLanguage(pref);
      })
      .catch(() => {});
  }, []);

  const setLang = (code) => {
    i18n.changeLanguage(code); // detector caches to localStorage samtwo_lang
    applyDir(code);
    syncProfileLanguage(code);
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, languages: LANGUAGES }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => useContext(LanguageContext);
